import { useRef, useEffect } from 'preact/hooks';
import type { Message, I18nStrings, LeaveMessageRequest } from '@bitio-builder/shared';
import { InlineContactPrompt } from './InlineContactPrompt';

interface MessageListProps {
  messages: Message[];
  isLoading?: boolean;
  showNoReplyFallback?: boolean;
  strings?: I18nStrings;
  onSubmitLeaveMessage?: (data: LeaveMessageRequest) => Promise<boolean>;
}

const containerStyle: Record<string, string | number> = {
  flex: 1,
  overflowY: 'auto',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const messageBaseStyle: Record<string, string | number> = {
  maxWidth: '85%',
  padding: '8px 16px',
  borderRadius: '16px',
  fontSize: '14px',
  lineHeight: 1.5,
  wordWrap: 'break-word',
};

const userMessageStyle: Record<string, string | number> = {
  ...messageBaseStyle,
  alignSelf: 'flex-end',
  background: 'var(--bb-primary, #4D5D32)',
  color: 'white',
  borderBottomRightRadius: '4px',
};

const assistantMessageStyle: Record<string, string | number> = {
  ...messageBaseStyle,
  alignSelf: 'flex-start',
  background: 'white',
  color: 'var(--bb-foreground, #1A1C17)',
  border: '1px solid var(--bb-border, #E5E7EB)',
  borderBottomLeftRadius: '4px',
};

const senderNameStyle: Record<string, string | number> = {
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--bb-primary, #4D5D32)',
  marginBottom: '4px',
};

const attachmentContainerStyle: Record<string, string | number> = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '6px',
  marginTop: '8px',
};

const attachmentStyle: Record<string, string | number> = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 10px',
  background: 'rgba(255,255,255,0.15)',
  borderRadius: '6px',
  fontSize: '12px',
  textDecoration: 'none',
  color: 'inherit',
  cursor: 'pointer',
  maxWidth: '180px',
};

const attachmentStyleAssistant: Record<string, string | number> = {
  ...attachmentStyle,
  background: 'var(--bb-background, #F5F5F5)',
  border: '1px solid var(--bb-border, #E5E7EB)',
};

const typingIndicatorStyle: Record<string, string | number> = {
  display: 'flex',
  gap: '4px',
  padding: '8px 16px',
  background: 'white',
  border: '1px solid var(--bb-border, #E5E7EB)',
  borderRadius: '16px',
  borderBottomLeftRadius: '4px',
  alignSelf: 'flex-start',
};

const dotStyle: Record<string, string | number> = {
  width: '8px',
  height: '8px',
  background: 'var(--bb-muted, #6B7280)',
  borderRadius: '50%',
  animation: 'bounce 1.4s infinite ease-in-out',
};

// File type icons
const FileIcon = ({ type }: { type: string }) => {
  const isImage = type.startsWith('image/');
  const isPdf = type === 'application/pdf';
  const isWord = type.includes('word');

  if (isImage) {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
    );
  }

  if (isPdf) {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <path d="M9 15h6" />
        <path d="M9 11h6" />
      </svg>
    );
  }

  if (isWord) {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    );
  }

  // Default document icon
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
};

// Extract filename from URL or generate one
function getFilenameFromUrl(url: string, index: number): string {
  try {
    const pathname = new URL(url).pathname;
    const filename = pathname.split('/').pop();
    if (filename && filename.includes('.')) {
      return filename;
    }
  } catch {
    // URL parsing failed
  }
  return `Fil ${index + 1}`;
}

// Get file type from URL
function getFileTypeFromUrl(url: string): string {
  const ext = url.split('.').pop()?.toLowerCase();
  const typeMap: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    txt: 'text/plain',
  };
  return typeMap[ext || ''] || 'application/octet-stream';
}

export function MessageList({ messages, isLoading, showNoReplyFallback, strings, onSubmitLeaveMessage }: MessageListProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  return (
    <div style={containerStyle} ref={containerRef}>
      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1); }
        }
      `}</style>
      {messages.map((message) => {
        const isUser = message.role === 'user';
        const attachments = message.images || [];
        const showSenderName = !isUser && message.source === 'slack' && message.senderName;

        return (
          <div
            key={message.id}
            style={isUser ? userMessageStyle : assistantMessageStyle}
          >
            {showSenderName && (
              <div style={senderNameStyle}>{message.senderName}</div>
            )}
            {message.content && <div>{message.content}</div>}
            {attachments.length > 0 && (
              <div style={attachmentContainerStyle}>
                {attachments.map((url, i) => {
                  const filename = getFilenameFromUrl(url, i);
                  const fileType = getFileTypeFromUrl(url);
                  const style = isUser ? attachmentStyle : attachmentStyleAssistant;

                  return (
                    <a
                      key={i}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={style}
                      title={`Ladda ner ${filename}`}
                    >
                      <FileIcon type={fileType} />
                      <span style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {filename}
                      </span>
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      {isLoading && (
        <div style={typingIndicatorStyle}>
          <span style={{ ...dotStyle, animationDelay: '-0.32s' }} />
          <span style={{ ...dotStyle, animationDelay: '-0.16s' }} />
          <span style={dotStyle} />
        </div>
      )}
      {showNoReplyFallback && strings && onSubmitLeaveMessage && (
        <InlineContactPrompt strings={strings} onSubmit={onSubmitLeaveMessage} />
      )}
    </div>
  );
}
